import React, { useEffect, useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import RHFTextArea from '../../../controls/tempRHFControls/RHFTextArea';
import useFetch from 'use-http';
import PersonnelToast from '../../components/PersonnelToast';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import EmployeeBenefitCodes from 'src/models/personnel/EmployeeBenefitCodes';
import BenefitCode from 'src/models/personnel/BenefitCode';
import EmployeeBenefits from 'src/models/personnel/EmployeeBenefits';

interface IPaperworkCommentDialogProps {
    dialogName: string;
    hideDialog: () => void;
    updateBenefit: () => void;
    showToast: (message: string, color?: string, persistent?: boolean) => void;
    benefitCodes: EmployeeBenefitCodes;
    benefit: EmployeeBenefits;
}

type DropDownSelection = {
    code: string,
    dscr: string
};

const EditBenefitDialog = (props: IPaperworkCommentDialogProps) => {
    const { register, errors, setValue, getValues } = useForm();
    // const { register, errors, setValue, getValues, reset } = useForm();
    const { put, response } = useFetch();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);
    // const [localBenefitCodes, setLocalBenefitCodes] = useState
    const [codeOptions, setCodeOptions] = useState<Array<BenefitCode>>();

    // filter list provided by hr - these lists are the current code options
    // TODO: will most likely change, waiting on new list (5/15/2026)
    const dentalFilter = ['EC', 'EF', 'EO', 'ES'];
    const healthFilter = ['EC', 'EF', 'EO', 'ES'];
    const lifeFilter = ['50', '25', '10'];
    const visionFilter = ['EC', 'EF', 'EO', 'ES'];
    // no filter for disability
    const wcFilter = ['YES'];
    const vacationFilter = ['15', '20'];
    const personalFilter = ['03'];
    const holidayFilter = ['08'];
    const taxFilter = ['YES'];
    const _401kFilter = ['S5'];

    // selects proper benefit options based on row editing
    useEffect(() => {
        if (props.benefit) {
            setValue('type', props?.benefit?.type);
            setValue('category', props?.benefit?.category);
            setValue('isRequired', props?.benefit?.isRequired);
            setValue('code', props?.benefit?.code);
            setValue('method', props?.benefit?.method);
            setValue('flatAmount', props?.benefit?.flatAmount);
            setValue('notes', props?.benefit?.benefitNotes);

            switch (props?.benefit?.type) {
                case 'INS':
                    let tempCodeOptions = props.benefitCodes?.insurance?.filter(code => code?.category === props.benefit.category);
                    switch (props?.benefit?.category) {
                        case 'HEALTH':
                            tempCodeOptions = tempCodeOptions.filter(code => healthFilter.includes(code.code));
                            break;
                        case 'DENTAL':
                            tempCodeOptions = tempCodeOptions.filter(code => dentalFilter.includes(code.code));
                            break;
                        case 'LIFE':
                            tempCodeOptions = tempCodeOptions.filter(code => lifeFilter.includes(code.code));
                            break;
                        case 'VISION':
                            tempCodeOptions = tempCodeOptions.filter(code => visionFilter.includes(code.code));
                            break;
                        default:
                            break;
                    }
                    setCodeOptions(tempCodeOptions);
                    break;
                case 'PTO':
                    let tempPtoCodeOptions = props.benefitCodes?.pto?.filter(code => code?.category === props.benefit.category);
                    switch (props?.benefit?.category) {
                        case 'VACATION':
                            tempPtoCodeOptions = tempPtoCodeOptions.filter(code => vacationFilter.includes(code.code));
                            break;
                        case 'PERSONAL':
                            tempPtoCodeOptions = tempPtoCodeOptions.filter(code => personalFilter.includes(code.code));
                            break;
                        case 'HOLIDAY':
                            tempPtoCodeOptions = tempPtoCodeOptions.filter(code => holidayFilter.includes(code.code));
                            break;
                        default:
                            break;
                    }
                    setCodeOptions(tempPtoCodeOptions);
                    break;
                case 'RET':
                    setCodeOptions(props.benefitCodes?.retirement?.filter(code => code?.category === props.benefit.category && _401kFilter.includes(code.code)));
                    break;
                case 'TAX':
                    setCodeOptions(props.benefitCodes?.tax?.filter(code => code?.category === props.benefit.category && taxFilter.includes(code.code)));
                    break;
                case 'WC':
                    setCodeOptions(props.benefitCodes?.workersComp?.filter(code => code?.category === props.benefit.category && wcFilter.includes(code.code)));
                    break;
                default:
                    setCodeOptions(null);
                    showToast('Unable to find code');
                    break;
            }
        }
    }, [props.benefit]);

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    const handleSubmit = () => {
        let formValues = getValues();
        let newData: EmployeeBenefits = {...props.benefit};

        newData.code = formValues.code;
        newData.flatAmount = parseFloat(formValues.flatAmount);
        newData.method = formValues.method;
        newData.benefitNotes = formValues.notes;

        updateBenefit(newData);
    };

    const updateBenefit = async (data: EmployeeBenefits) => {
        await put(`/v1.0/landmen/benefits/update-employee-benefit-detail`, data);

        if (response.ok) {
            props.showToast('Successfully updated benefit');
            props.updateBenefit();
        } else {
            showToast('Unable to update benefit: ' + response, `rgba(255,0,0,0.5)`);
        }
    };

    const benefitCodeDropdown = () => {
        return codeOptions
            ? codeOptions?.map((ct: DropDownSelection) => {
                return { value: ct.code, label: ct.dscr };
            })
            : [];
    };

    return (
        <Dialog title="Edit Benefit" dialogName={props.dialogName} bodyStyle={{ width: '5%' }}>
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <div>
                <form onSubmit={handleSubmit}>
                    <RHFInputBox
                        name={'type'}
                        label={'Benefit Type'}
                        labelOnLeft={true}
                        register={register}
                        errors={errors}
                        disabled={true}
                        isRequired={true}
                        spaceLabelContent={true}
                    />
                    <RHFInputBox
                        name={'isRequired'}
                        label={'Required'}
                        labelOnLeft={true}
                        register={register}
                        errors={errors}
                        disabled={true}
                        isRequired={true}
                        spaceLabelContent={true}
                    />
                    <RHFInputBox
                        name={'category'}
                        label={'Category'}
                        labelOnLeft={true}
                        register={register}
                        errors={errors}
                        disabled={true}
                        isRequired={true}
                        spaceLabelContent={true}
                    />
                    <RHFSelect
                        name={'code'}
                        label={'Code'}
                        labelOnLeft={true}
                        register={register}
                        errors={errors}
                        isRequired={true}
                        options={benefitCodeDropdown()}
                        spaceLabelContent={true}
                    />
                    <RHFSelect
                        name={'method'}
                        label={'Method'}
                        labelOnLeft={true}
                        register={register}
                        errors={errors}
                        isRequired={true}
                        options={[
                            { value: 'T', label: 'Table Lookup' },
                            { value: 'F', label: 'Flat Rate' },
                        ]}
                        spaceLabelContent={true}
                    />
                    <RHFInputBox
                        name={'flatAmount'}
                        label={'Annual Flat Rate'}
                        labelOnLeft={true}
                        register={register}
                        errors={errors}
                        isRequired={true}
                        spaceLabelContent={true}
                    />
                    <div className="form-row">
                        <label>Notes</label>
                        <RHFTextArea
                            name={'notes'}
                            register={register}
                            errors={errors}
                            rows={1}
                        />
                    </div>
                </form>
            </div>
            <div className="button-row" style={{ paddingBottom: '1rem' }}>
                <Button text="Save" onClick={handleSubmit} intent="primary" />
                <Button text="Cancel" onClick={() => props.hideDialog()} intent="secondary" />
            </div>
        </Dialog>
    );
};

export default EditBenefitDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/benefits/EditBenefitDialog.tsx